/**
 * MappingRulesEditor — view and edit narration → ledger mapping rules.
 *
 * Two tabs:
 *   Global  — apply to ALL companies, checked first (highest priority).
 *   Company — apply only to the active company, checked second.
 *
 * Each row has an enabled toggle so rules can be paused without deleting.
 * Row order = priority (top = checked first, first match wins).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  X, Plus, Trash2, GripVertical, AlertTriangle, Save,
  Loader2, Eye, EyeOff,
} from 'lucide-react';
import {
  getMappingRules, saveMappingRules,
  getGlobalRules,  saveGlobalRules,
  type MappingRule,
} from '@/lib/aiaccountant';
import clsx from 'clsx';

interface Props {
  open:      boolean;
  companyId: string;   // empty string = global rules only
  onClose:   () => void;
}

type TxnType = 'Both' | 'Debit' | 'Credit';
type TabId   = 'global' | 'company';

interface Row extends MappingRule {
  _id: number; // stable local key for React
}

let _seq = 0;
const uid = () => ++_seq;

const EMPTY_ROW = (): Row => ({
  _id: uid(), contains: '', ledger: '', txn_type: 'Both', notes: '', enabled: true,
});

export function MappingRulesEditor({ open, companyId, onClose }: Props) {
  // Active tab: if no company set, force global; else start on company
  const [tab, setTab]         = useState<TabId>(companyId ? 'company' : 'global');
  const [globalRows, setGlobalRows]   = useState<Row[]>([]);
  const [companyRows, setCompanyRows] = useState<Row[]>([]);
  const [loading, setLoading]         = useState(false);
  const [saving,  setSaving]          = useState(false);
  const [error,   setError]           = useState('');
  const [dirty,   setDirty]           = useState(false);
  const [dragIdx, setDragIdx]         = useState<number | null>(null);

  const rows    = tab === 'global' ? globalRows    : companyRows;
  const setRows = tab === 'global' ? setGlobalRows : setCompanyRows;

  // Load both rule sets whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setDirty(false);
    setTab(companyId ? 'company' : 'global');

    const loadGlobal  = getGlobalRules().then((r) => setGlobalRows(r.map((x) => ({ ...x, _id: uid() }))));
    const loadCompany = companyId
      ? getMappingRules(companyId).then((r) => setCompanyRows(r.map((x) => ({ ...x, _id: uid() }))))
      : Promise.resolve();

    Promise.all([loadGlobal, loadCompany])
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [open, companyId]);

  // Esc closes (unless dirty)
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !dirty) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, dirty, onClose]);

  const mark = useCallback(() => setDirty(true), []);

  const updateRow = (idx: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    mark();
  };
  const addRow    = () => { setRows((prev) => [...prev, EMPTY_ROW()]); mark(); };
  const deleteRow = (idx: number) => { setRows((prev) => prev.filter((_, i) => i !== idx)); mark(); };

  // Drag-to-reorder
  const onDragStart = (idx: number) => setDragIdx(idx);
  const onDragOver  = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setDragIdx(idx);
    mark();
  };
  const onDragEnd = () => setDragIdx(null);

  const validate = (): string => {
    for (let i = 0; i < rows.length; i++) {
      if (!rows[i].contains.trim()) return `Row ${i + 1}: "Narration contains" is required.`;
      if (!rows[i].ledger.trim())   return `Row ${i + 1}: "Ledger name" is required.`;
    }
    return '';
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    try {
      if (tab === 'global') {
        await saveGlobalRules(globalRows.map(({ _id: _, ...r }) => r));
      } else {
        await saveMappingRules(companyId, companyRows.map(({ _id: _, ...r }) => r));
      }
      setDirty(false);
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleBackdrop = () => {
    if (dirty && !confirm('You have unsaved changes. Discard and close?')) return;
    onClose();
  };

  if (!open) return null;

  const enabledCount  = rows.filter((r) => r.enabled).length;
  const disabledCount = rows.length - enabledCount;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl flex flex-col"
        style={{ width: 'min(860px, calc(100vw - 2rem))', maxHeight: 'calc(100vh - 4rem)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div>
            <h2 className="font-semibold text-sm">Mapping Rules</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Rules run <strong>before</strong> AI. Top row = highest priority. First enabled match wins.
              Global rules are checked before company rules.
            </p>
          </div>
          <button onClick={handleBackdrop} className="p-1 rounded hover:bg-muted" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-border px-5 shrink-0">
          <TabButton
            active={tab === 'global'}
            onClick={() => { if (tab !== 'global') { setError(''); setTab('global'); } }}
            label="Global"
            count={globalRows.length}
            hint="Applies to all companies — checked first"
          />
          {companyId && (
            <TabButton
              active={tab === 'company'}
              onClick={() => { if (tab !== 'company') { setError(''); setTab('company'); } }}
              label={companyId}
              count={companyRows.length}
              hint="Applies to this company only — checked after global"
            />
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading rules…
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div
                className="grid gap-2 mb-1 text-xs font-medium text-muted-foreground px-1"
                style={{ gridTemplateColumns: '20px 32px 1fr 1fr 90px 32px' }}
              >
                <span />
                <span title="Enable / disable without deleting">On</span>
                <span>If narration contains</span>
                <span>Ledger name (exact, as in Tally)</span>
                <span>Type</span>
                <span />
              </div>

              {rows.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">
                  No rules yet.{' '}
                  {tab === 'global'
                    ? 'Add global rules that apply across all companies.'
                    : 'Add company-specific rules — these run after global rules.'}
                </div>
              )}

              {rows.map((row, idx) => (
                <div
                  key={row._id}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => onDragOver(e, idx)}
                  onDragEnd={onDragEnd}
                  className={clsx(
                    'grid gap-2 items-center mb-1.5 rounded px-1 py-0.5 transition-colors',
                    !row.enabled && 'opacity-50',
                    dragIdx === idx ? 'bg-primary/10' : 'hover:bg-muted/40',
                  )}
                  style={{ gridTemplateColumns: '20px 32px 1fr 1fr 90px 32px' }}
                >
                  {/* Drag handle */}
                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground cursor-grab shrink-0" />

                  {/* Enabled toggle */}
                  <button
                    onClick={() => updateRow(idx, { enabled: !row.enabled })}
                    className={clsx(
                      'p-1 rounded',
                      row.enabled
                        ? 'text-primary hover:bg-primary/10'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                    title={row.enabled ? 'Disable rule (skip without deleting)' : 'Enable rule'}
                    aria-label={row.enabled ? 'Disable' : 'Enable'}
                  >
                    {row.enabled
                      ? <Eye className="w-3.5 h-3.5" />
                      : <EyeOff className="w-3.5 h-3.5" />}
                  </button>

                  {/* Narration contains */}
                  <input
                    type="text"
                    value={row.contains}
                    onChange={(e) => updateRow(idx, { contains: e.target.value })}
                    placeholder="e.g. ATM CASH"
                    className={clsx(
                      'px-2 py-1.5 text-xs rounded border bg-background outline-none focus:ring-2 focus:ring-ring',
                      !row.contains.trim() ? 'border-destructive/60' : 'border-border',
                    )}
                  />

                  {/* Ledger name */}
                  <input
                    type="text"
                    value={row.ledger}
                    onChange={(e) => updateRow(idx, { ledger: e.target.value })}
                    placeholder="e.g. Bank Charges"
                    className={clsx(
                      'px-2 py-1.5 text-xs rounded border bg-background outline-none focus:ring-2 focus:ring-ring',
                      !row.ledger.trim() ? 'border-destructive/60' : 'border-border',
                    )}
                  />

                  {/* Transaction type */}
                  <select
                    value={row.txn_type}
                    onChange={(e) => updateRow(idx, { txn_type: e.target.value as TxnType })}
                    className="px-2 py-1.5 text-xs rounded border border-border bg-background outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="Both">Both</option>
                    <option value="Debit">Debit</option>
                    <option value="Credit">Credit</option>
                  </select>

                  {/* Delete */}
                  <button
                    onClick={() => deleteRow(idx)}
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    aria-label="Delete row"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {/* Add row */}
              <button
                onClick={addRow}
                className="mt-2 flex items-center gap-1.5 text-xs text-primary hover:underline px-1"
              >
                <Plus className="w-3.5 h-3.5" /> Add row
              </button>

              {/* Suspense warning */}
              {rows.some((r) => r.enabled && r.ledger.toLowerCase().startsWith('suspense')) && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Some enabled rules map to <strong>Suspense</strong>. These skip AI entirely —
                    disable them to let AI classify these transactions instead.
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mb-2 text-xs text-destructive bg-destructive/10 rounded px-3 py-2">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/30 shrink-0">
          <span className="text-xs text-muted-foreground">
            {enabledCount} active{disabledCount > 0 ? `, ${disabledCount} disabled` : ''} rule{rows.length !== 1 ? 's' : ''}
            {dirty ? ' · unsaved changes' : ''}
          </span>
          <div className="flex gap-2">
            <button onClick={handleBackdrop} className="px-3 py-1.5 text-sm rounded hover:bg-muted">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              className="px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                : <><Save className="w-3.5 h-3.5" /> Save Rules</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active, onClick, label, count, hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  hint: string;
}) {
  return (
    <button
      onClick={onClick}
      title={hint}
      className={clsx(
        'px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
        active
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
      <span className={clsx(
        'ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px]',
        active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
      )}>
        {count}
      </span>
    </button>
  );
}
